import React from 'react'
import isNil from 'lodash/isNil'
import styles from './TruncatedFileName.module.scss'

const EXTENSION = /\.[a-z0-9]{2,5}$/i
const UUID_PREFIX =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}[-_\s]*)+/i
const FILLER = /funding\s+invoice/gi
const SEPARATORS = /[_]+/g
const escapeRegExp = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** Matches the company even when the file joins its words with "-" or "_". */
const companyPattern = (companyName: string) =>
  new RegExp(
    companyName.trim().split(/\s+/).map(escapeRegExp).join('[\\s_-]+'),
    'i'
  )

/**
 * Short label for an uploaded invoice file: drops the extension, any leading
 * IDs, the company name (already in its own column) and the "Funding Invoice"
 * filler, and turns underscores into dots. "Acme_Funding Invoice_January
 * 2027_OEP.xlsx" becomes "January 2027 · OEP". Falls back to the bare name if
 * nothing would be left.
 */
export const toFriendlyFileName = (
  fileName: string,
  companyName?: string | null
): string => {
  const bare = fileName.replace(EXTENSION, '')
  let label = bare.replace(UUID_PREFIX, '')
  if (!isNil(companyName) && companyName.trim().length > 0) {
    label = label.replace(companyPattern(companyName), '')
  }
  label = label
    .replace(FILLER, '')
    .split(SEPARATORS)
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(' · ')
  return label.length > 0 ? label : bare
}

interface TruncatedFileNameProps {
  name: string
  /** Company shown elsewhere on the row; removed from the label when present. */
  companyName?: string | null
}

/** Readable file label with the full file name on hover. */
const TruncatedFileName: React.FC<TruncatedFileNameProps> = ({
  name,
  companyName,
}) => (
  <span className={styles.root} title={name}>
    {toFriendlyFileName(name, companyName)}
  </span>
)

export { TruncatedFileName }
